'use client';

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { subscribeWithSelector } from 'zustand/middleware';

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  cmd?: boolean;
  shift?: boolean;
  alt?: boolean;
  description: string;
  category?: string;
  action: () => void;
  global?: boolean;
  enabled?: boolean;
}

interface KeyboardShortcutsState {
  shortcuts: Map<string, KeyboardShortcut>;
  showHelp: boolean;
  isEnabled: boolean;
  categories: Set<string>;
}

interface KeyboardShortcutsActions {
  addShortcut: (shortcut: KeyboardShortcut) => void;
  addShortcuts: (shortcuts: KeyboardShortcut[]) => void;
  removeShortcut: (key: string) => void;
  removeShortcuts: (keys: string[]) => void;
  updateShortcut: (key: string, updates: Partial<KeyboardShortcut>) => void;
  toggleHelp: () => void;
  showHelpPanel: () => void;
  hideHelpPanel: () => void;
  setEnabled: (enabled: boolean) => void;
  clearAll: () => void;
  getShortcutsByCategory: (category: string) => KeyboardShortcut[];
  isShortcutRegistered: (key: string) => boolean;
  executeShortcut: (key: string) => void;
}

type KeyboardShortcutsStore = KeyboardShortcutsState & KeyboardShortcutsActions;

const initialState: KeyboardShortcutsState = {
  shortcuts: new Map(),
  showHelp: false,
  isEnabled: true,
  categories: new Set(),
};

function getShortcutId(
  shortcut: Pick<KeyboardShortcut, 'key' | 'ctrl' | 'cmd' | 'shift' | 'alt'>
): string {
  return `${shortcut.key}-${!!shortcut.ctrl}-${!!shortcut.cmd}-${!!shortcut.shift}-${!!shortcut.alt}`;
}

export const useKeyboardShortcutsStore = create<KeyboardShortcutsStore>()(
  subscribeWithSelector(
    immer((set, get) => ({
      ...initialState,

      addShortcut: (shortcut: KeyboardShortcut) => {
        set((state) => {
          const id = getShortcutId(shortcut);
          state.shortcuts.set(id, {
            ...shortcut,
            enabled: shortcut.enabled !== false,
          });
          if (shortcut.category) {
            state.categories.add(shortcut.category);
          }
        });
      },

      addShortcuts: (shortcuts: KeyboardShortcut[]) => {
        set((state) => {
          shortcuts.forEach((shortcut) => {
            const id = getShortcutId(shortcut);
            state.shortcuts.set(id, {
              ...shortcut,
              enabled: shortcut.enabled !== false,
            });
            if (shortcut.category) {
              state.categories.add(shortcut.category);
            }
          });
        });
      },

      removeShortcut: (key: string) => {
        set((state) => {
          // Remove all shortcuts with this key, regardless of modifiers
          const toRemove: string[] = [];
          state.shortcuts.forEach((shortcut, id) => {
            if (shortcut.key === key) {
              toRemove.push(id);
            }
          });
          toRemove.forEach((id) => state.shortcuts.delete(id));
        });
      },

      removeShortcuts: (keys: string[]) => {
        set((state) => {
          keys.forEach((key) => {
            const toRemove: string[] = [];
            state.shortcuts.forEach((shortcut, id) => {
              if (shortcut.key === key) {
                toRemove.push(id);
              }
            });
            toRemove.forEach((id) => state.shortcuts.delete(id));
          });
        });
      },

      updateShortcut: (key: string, updates: Partial<KeyboardShortcut>) => {
        set((state) => {
          state.shortcuts.forEach((shortcut, id) => {
            if (shortcut.key === key) {
              const updated = { ...shortcut, ...updates };
              state.shortcuts.set(id, updated);
              if (updated.category && updated.category !== shortcut.category) {
                state.categories.add(updated.category);
              }
            }
          });
        });
      },

      toggleHelp: () => {
        set((state) => {
          state.showHelp = !state.showHelp;
        });
      },

      showHelpPanel: () => {
        set((state) => {
          state.showHelp = true;
        });
      },

      hideHelpPanel: () => {
        set((state) => {
          state.showHelp = false;
        });
      },

      setEnabled: (enabled: boolean) => {
        set((state) => {
          state.isEnabled = enabled;
        });
      },

      clearAll: () => {
        set((state) => {
          state.shortcuts.clear();
          state.categories.clear();
          state.showHelp = false;
        });
      },

      getShortcutsByCategory: (category: string) => {
        const state = get();
        return Array.from(state.shortcuts.values()).filter(
          (shortcut) =>
            shortcut.category === category && shortcut.enabled !== false
        );
      },

      isShortcutRegistered: (key: string) => {
        const state = get();
        return Array.from(state.shortcuts.values()).some(
          (shortcut) => shortcut.key === key
        );
      },

      executeShortcut: (key: string) => {
        const state = get();
        if (!state.isEnabled) return;

        const shortcut = Array.from(state.shortcuts.values()).find(
          (s) => s.key === key && s.enabled !== false
        );

        if (shortcut) {
          shortcut.action();
        }
      },
    }))
  )
);
